import React, { FC, useEffect, useRef, useState } from 'react'
import debounce from 'lib/debounce'
import Portal from './Portal'
import './helpers'
import SAAlert from 'components/SA/Alert'
import { getMessages, getType } from 'selectors/shared/flashMessage'
import { Actions as FlashAction } from 'actions/shared/flashMessage'
import { useDispatch, useSelector } from 'react-redux'
import { getPagePath } from 'selectors/page'
import { FlashMessageType } from 'types/Shared/FlashMessage'

const messageDebounce = debounce()
const cleanupDebounce = debounce()
const MESSAGE_DURATION = 3500
const CLEANUP_DURATION = 500
const MAX_STACKED_MESSAGES = 3

type StackedMessage = {
  id: number
  text: string
  type: FlashMessageType
}

type FlashMessageProps = {
  shouldAutoClear?: boolean
}

const FlashMessage: FC<FlashMessageProps> = (props) => {
  const { shouldAutoClear = true } = props
  const dispatch = useDispatch()
  const type = useSelector(getType)
  const messages = useSelector(getMessages)
  const path = useSelector(getPagePath)

  const [isVisible, setIsVisible] = useState(true)
  const [stack, setStack] = useState<StackedMessage[]>([])
  const idRef = useRef(0)
  const prevStackLenRef = useRef(0)
  const prevPathRef = useRef<string | null>(null)

  const clearMessage = () => dispatch(FlashAction.CLEAR())

  useEffect(() => {
    if (prevPathRef.current !== null && prevPathRef.current !== path) {
      clearMessage()
      setStack([])
      setIsVisible(true)
    }
    prevPathRef.current = path
  }, [path])

  const closeFlash = (ev?: any) => {
    if (ev) ev.preventDefault()
    cleanupDebounce(() => {
      clearMessage()
      setStack([])
      setIsVisible(true)
    }, CLEANUP_DURATION)
    setIsVisible(false)
  }

  useEffect(() => {
    if (messages.length === 0) {
      prevStackLenRef.current = 0
      return
    }

    const startIdx = messages.length > prevStackLenRef.current ? prevStackLenRef.current : 0
    prevStackLenRef.current = messages.length

    const incoming = messages.slice(startIdx)
    if (incoming.length === 0) return

    setStack((prev) => {
      const next = [...prev, ...incoming.map((text) => ({ id: ++idRef.current, text, type }))]
      return next.length > MAX_STACKED_MESSAGES ? next.slice(-MAX_STACKED_MESSAGES) : next
    })

    if (shouldAutoClear) {
      messageDebounce(() => {
        closeFlash()
      }, MESSAGE_DURATION)
    }
  }, [messages])

  return (
    <>
      {stack.length > 0 && (
        <Portal
          {...props}
          closeFlash={shouldAutoClear ? closeFlash : () => {}}
          isVisible={isVisible}
        >
          {stack.map(({ id, text, type: msgType }) => (
            <SAAlert
              key={id}
              type={msgType}
              text={text}
              onClose={
                shouldAutoClear
                  ? closeFlash
                  : () => setStack((prev) => prev.filter((m) => m.id !== id))
              }
              hasBorderRadius={false}
            />
          ))}
        </Portal>
      )}
    </>
  )
}

export default FlashMessage
